import { Box } from "@material-ui/core";
import { A, B, D, G } from "@mobily/ts-belt";
import { useAtom } from "jotai";
import PropTypes from "prop-types";
import * as React from "react";
import { useActiveInfluencer, useCurrentUser } from "../../../hooks";
import { Form, InfoTooltip } from "../../../theme";
import ProductSigners from "../ProductSigners";
import * as styles from "../styles";
import { format } from "../utils";
import ProductImageInput from "./ProductImageInput";
import CollectionPosition from "./CollectionPosition";
import { totalsFor, useCanEdit } from "./utils";

function onBlurTrim({ key, setProduct }) {
  return (evt) => {
    const { target } = evt;
    const newValue = evt.target.value.trim();
    setProduct(D.set(key, newValue));
    target.value = newValue;
  };
}

function ProductDetailsStep({ stateAtom, productAtom, productSignersAtom }) {
  const currentUser = useCurrentUser();
  const activeInfluencer = useActiveInfluencer();
  const [signers, setSigners] = useAtom(productSignersAtom);
  const canEdit = useCanEdit(signers);

  const currentSigner = React.useMemo(
    () =>
      signers.find(
        (signer) => signer.influencerId === activeInfluencer.influencerId,
      ),
    [signers, activeInfluencer],
  );

  const [position, setPosition] = React.useState(currentSigner?.position ?? "");
  const [product, setProduct] = useAtom(productAtom);

  const onBlurBump = (evt) => {
    const { target } = evt;
    const newValue = Math.round(Number(target.value)) * 100;
    setProduct(D.set("productBump", newValue));
    target.value = format(newValue);
  };

  const bump = React.useMemo(() => {
    const totals = totalsFor({ product, signers });

    if (product.fixedBump) {
      return format(product.productBump);
    }

    if (totals.bump !== product.productBump) {
      return format(totals.bump);
    }

    return format(product.productBump);
  }, [product, signers]);

  React.useEffect(() => {
    setPosition(currentSigner?.position ?? "");
  }, [currentSigner]);

  return (
    <Box display="flex" flexDirection="column">
      <ProductImageInput
        disabled={!canEdit}
        productAtom={productAtom}
        stateAtom={stateAtom}
      />

      <Box mt={2} display="flex" flexDirection="column" gridGap="8px">
        <styles.Label>Name</styles.Label>

        <div>
          <Form.Input
            disabled={!canEdit}
            fullWidth
            size="small"
            value={product.name}
            placeholder="Product Name"
            inputProps={{ maxLength: 100 }}
            onChange={(evt) => setProduct(D.set("name", evt.target.value))}
            onBlur={onBlurTrim({ key: "name", setProduct })}
          />

          <styles.InputLengthStatus value={product.name} length={100} />
        </div>
      </Box>

      <Box display="flex" flexDirection="column" gridGap="8px">
        <styles.Label>Description</styles.Label>

        <div>
          <Form.Input
            disabled={!canEdit}
            fullWidth
            value={G.isString(product.subtext) ? product.subtext : ""}
            size="small"
            multiline
            rows={3}
            placeholder="Add a description of what you're selling"
            onChange={(evt) => setProduct(D.set("subtext", evt.target.value))}
            onBlur={onBlurTrim({ key: "subtext", setProduct })}
            inputProps={{ maxLength: 250 }}
          />

          <styles.InputLengthStatus value={product.subtext} length={250} />
        </div>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        gridGap="16px"
        width="100%"
      >
        <Box display="flex" flexDirection="column" gridGap="8px">
          <styles.Label>Quantity</styles.Label>

          <Box mb={1} display="flex" alignItems="center" gridGap="16px">
            <Form.Input
              value={product.stock ?? ""}
              disabled={!canEdit || product.stock === null}
              size="small"
              placeholder={product.stock === null ? "N/A" : "Quantity"}
              onChange={(evt) =>
                setProduct(D.set("stock", Number(evt.target.value)))
              }
              inputProps={{
                type: "number",
                min: 0,
                max: 9999,
                size: 3,
                maxLength: 4,
                step: 1,
              }}
              error={product.stock < 0}
            />

            <Form.SmallCheckbox
              disabled={!canEdit}
              checked={product.stock === null}
              onChange={() =>
                setProduct(D.set("stock", product.stock === null ? 0 : null))
              }
              label="Unlimited"
            />
          </Box>
        </Box>

        {currentUser.isAdmin && (
          <Box width={250} display="flex" flexDirection="column" gridGap="8px">
            <styles.Label>Product Bump</styles.Label>

            <Box mb={1} display="flex" alignItems="center" gridGap="16px">
              <Form.Input
                value={bump}
                disabled={!product.fixedBump}
                size="small"
                placeholder="Bump"
                InputProps={{ startAdornment: "$" }}
                onChange={(evt) => {
                  const newValue = Math.round(Number(evt.target.value)) * 100;
                  setProduct(D.set("productBump", newValue));
                }}
                onBlur={onBlurBump}
                inputProps={{ type: "number", size: 2, min: 0, maxLength: 4 }}
              />

              <Form.SmallCheckbox
                checked={Boolean(product.fixedBump)}
                onChange={() => setProduct(D.update("fixedBump", B.not))}
                label="Set Fixed Bump"
              />
            </Box>
          </Box>
        )}
      </Box>

      {!activeInfluencer.isGroupShop && (
        <Box display="flex" flexDirection="column" gridGap="8px" my={1}>
          <div>
            <styles.Label>
              <span>Shop Position</span>
              <InfoTooltip
                size="small"
                content="Lower number to move to the top of the shop, higher to move to the bottom."
              />
            </styles.Label>
          </div>

          <div>
            <Form.Input
              type="text"
              size="small"
              variant="outlined"
              disabled={!currentSigner}
              value={position}
              inputProps={{
                type: "number",
                min: 1,
                size: 3,
                maxLength: 4,
                step: 1,
              }}
              onChange={(evt) => {
                setPosition(evt.target.value);
              }}
              onBlur={(evt) => {
                if (!currentSigner) {
                  return;
                }

                const trimmed = evt.target.value.trim();

                if (!trimmed) {
                  return;
                }

                const newNumber = Number.parseInt(trimmed, 10);

                if (Number.isNaN(newNumber)) {
                  return;
                }

                const fn = A.map((signer) =>
                  signer.influencerId === currentSigner.influencerId
                    ? D.merge(signer, { position: newNumber })
                    : signer,
                );

                setSigners(fn);
              }}
            />
          </div>
        </Box>
      )}

      {activeInfluencer.isGroupShop &&
        activeInfluencer.productTagString &&
        product.productId > 0 && (
          <CollectionPosition
            tagString={activeInfluencer.productTagString}
            productId={product.productId}
          />
        )}

      <ProductSigners
        productId={product.productId}
        productSignersAtom={productSignersAtom}
        productAtom={productAtom}
      />
    </Box>
  );
}

ProductDetailsStep.propTypes = {
  productAtom: PropTypes.object.isRequired,
  productSignersAtom: PropTypes.object.isRequired,
  stateAtom: PropTypes.object.isRequired,
};

export default {
  Component: ProductDetailsStep,
  title: "Product Details",
};
